import React from 'react';
import Box, {type BoxProps} from '@mui/material/Box';

import {ClassNames, GridAreaName} from './shared';
import {type AnchorProps, getAnchorProps} from './anchor-props';

export type ListRowProps = {
    children?: React.ReactNode;
    isSelected?: boolean;
    onClick?: BoxProps['onClick'];
} & AnchorProps;

export const ListRow = (props: ListRowProps) => {
    const anchorProps = getAnchorProps(props);

    return (
        <Box
            data-checked={props.isSelected}
            {...anchorProps}
            onClick={props.onClick}
            sx={theme => ({
                display: 'contents',

                // We prefer to use subgrid if it's supported as display: contents gives accessibility issues
                // E.g. it's not possible to tab through list's rows
                ['@supports (grid-template-rows: subgrid)']: {
                    gridTemplateColumns: 'subgrid',
                    gridColumn: GridAreaName,
                    display: 'grid',

                    // Not exactly sure why, but when adding an href to the row, the text of all the row's list items
                    // gets the underline styling by default. This is a workaround to remove it.
                    textDecoration: 'none',
                },

                [`& > .${ClassNames.ListItem}`]: {
                    height: '64px',
                    backgroundColor: theme.modernFamlyTheme.colorPalette.n0,
                    borderTop: `1px solid ${theme.modernFamlyTheme.colorPalette.n75}`,
                    borderBottom: `1px solid ${theme.modernFamlyTheme.colorPalette.n75}`,
                    alignItems: 'center',
                    '&:first-of-type': {
                        borderLeft: `1px solid ${theme.modernFamlyTheme.colorPalette.n75}`,
                        borderTopLeftRadius: '8px',
                        borderBottomLeftRadius: '8px',
                    },

                    '&:last-of-type': {
                        borderRight: `1px solid ${theme.modernFamlyTheme.colorPalette.n75}`,
                        borderTopRightRadius: '8px',
                        borderBottomRightRadius: '8px',
                    },
                },
                [`&[data-checked="true"] > .${ClassNames.ListItem}`]: {
                    backgroundColor: theme.modernFamlyTheme.colorPalette.p50,
                },

                // If the row is a link, make it look clickable by adding hover styling on its immediate children
                ...(props.href || props.onClick
                    ? {
                          cursor: 'pointer',
                          [`&:hover > .${ClassNames.ListItem}`]: {
                              backgroundColor: theme.modernFamlyTheme.colorPalette.p50,
                          },
                      }
                    : {}),
            })}
        >
            {props.children}
        </Box>
    );
};
